import { NativeResources } from '@ariksa/inventory-core';
import dayjs from 'dayjs';

export const VULNERABILITY_NODE_TYPES = new Set([
  'Endpoint',
  'OS',
  'Technology',
  'Severity',
]);

export const ACTIVITY_GRAPH_RESOURCE_NODE_SIZE = 40;
export const ACTIVITY_EDGE_RELATIONSHIPS = [
  'Used By',
  'Accessed By',
  'Contains',
];

export const getActivityActions = (actions: unknown) => {
  if (!actions) return [];
  return Array.isArray(actions) ? actions : [actions];
};

export const getActivityActionLabel = (action: unknown) => {
  if (typeof action === 'string' || typeof action === 'number') {
    return String(action);
  }

  if (action && typeof action === 'object') {
    const actionData = action as Record<string, unknown>;
    return String(
      actionData.action ?? actionData.name ?? actionData.display_name ?? '-',
    );
  }

  return '-';
};

export const getActivityEdgeNames = (
  _nodeIds: string[],
  relationships: Array<{
    source?: unknown;
    dest?: unknown;
    source_type?: unknown;
    dest_type?: unknown;
  }>,
) => {
  return relationships.map(relationship => {
    const normalizeType = (type: unknown) =>
      String(type ?? '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
    const sourceType = normalizeType(relationship.source_type);
    const destinationType = normalizeType(relationship.dest_type);
    const s3FolderTypes = ['awss3folder', 's3folder'];
    const storageType = normalizeType(NativeResources.SimpleStorageService);
    const roleType = normalizeType(NativeResources.IamRole);
    const credentialTypes = [
      normalizeType('Credentials'),
      normalizeType(NativeResources.IamUserAccessKey),
      'accesskey',
    ];
    const userType = normalizeType(NativeResources.IamUser);
    const accessKeyType = normalizeType(NativeResources.IamUserAccessKey);
    const serviceType = normalizeType(NativeResources.Service);
    const isIdentity =
      sourceType.includes('identity') ||
      sourceType.includes('user') ||
      sourceType.includes('principal');
    const isDestinationIdentity =
      destinationType.includes('identity') ||
      destinationType.includes('user') ||
      destinationType.includes('principal');

    if (s3FolderTypes.includes(sourceType) && destinationType === storageType) {
      return ACTIVITY_EDGE_RELATIONSHIPS[2];
    }

    if (
      (s3FolderTypes.includes(sourceType) && destinationType === roleType) ||
      (sourceType === roleType && s3FolderTypes.includes(destinationType)) ||
      (s3FolderTypes.includes(sourceType) &&
        credentialTypes.includes(destinationType)) ||
      (credentialTypes.includes(sourceType) &&
        s3FolderTypes.includes(destinationType))
    ) {
      return ACTIVITY_EDGE_RELATIONSHIPS[1];
    }

    if (
      (sourceType === roleType && destinationType === storageType) ||
      (sourceType === storageType && destinationType === roleType) ||
      (credentialTypes.includes(sourceType) &&
        destinationType === storageType) ||
      (sourceType === storageType && credentialTypes.includes(destinationType))
    ) {
      return ACTIVITY_EDGE_RELATIONSHIPS[1];
    }

    if (
      (sourceType === roleType && isDestinationIdentity) ||
      (isIdentity && destinationType === roleType) ||
      (sourceType === userType && destinationType === accessKeyType) ||
      (sourceType === accessKeyType && destinationType === userType) ||
      (sourceType === serviceType && destinationType === roleType) ||
      (sourceType === roleType && destinationType === serviceType)
    ) {
      return ACTIVITY_EDGE_RELATIONSHIPS[0];
    }

    return '';
  });
};

/* Shared filters for the activity-accesses list/download requests. Added so
   the identity count and the activity graph CSV download send the same body. */
export const getActivityAccessFilters = (
  dateRange?: { from?: Date; to?: Date },
  resourceArn?: string | String,
  objectPaths?: Array<{ value: string }>,
) => ({
  start_date: dateRange?.from
    ? dayjs(dateRange.from).format('YYYY-MM-DD')
    : undefined,
  end_date: dateRange?.to
    ? dayjs(dateRange.to).format('YYYY-MM-DD')
    : undefined,
  resource_arn: resourceArn,
  folders: objectPaths?.map(option => option.value),
});
